import "./NewsFeedComponent.scss";
import template from "./NewsFeedComponent.html";
import {IComponentOptions, IPostLink, IRootElementService} from "angular";
import {BehaviorSubject, Observable} from "rxjs";
import {Securities} from "../../../../instruments/types/CodeAndExchange";
import {
    isDateSeparator,
    isHeadlineModel,
    isDocumentModel,
    NewsHeadlineDataRow,
    NewsHeadlineDataRows,
    NewsHeadlineViewModel, isHtmlModel, isTextModel
} from "../../../models/NewsHeadlineViewModel";
import {NewsStore} from "../../../store/headlines/NewsStore";
import {NewsStoreFactory} from "../../../store/headlines/NewsStoreFactory";
import {identity} from "../../../../js/util/UtilFunctions";
import {ArrayUtil} from "../../../../js/util/ArrayUtil";
import {NewsItemId} from "../../../types/NewsItemId";
import {SelectedNewsSources} from "../../news-source-selector/model/SelectedNewsSources";
import {NewsItemDialogService} from "../../../service/dialog/NewsItemDialogService";
import {isTextHeadline} from "../../../models/NewsHeadlineRecord";

export const NewsFeed = "tfNewsFeed";

export function newsFeed(): IComponentOptions {
    return {
        template,
        bindings: {
            codes: "<",
            sources: "<",
            hideSecurity: "<"
        },
        controllerAs: "ctrl",
        controller: NewsFeedComponentController
    };
}

export class NewsFeedComponentController implements IPostLink {

    /** @input */
    public set codes(codes: Securities | null) {
        this.store.updateCode(codes);
    }

    /** @input */
    public set sources(sources: SelectedNewsSources | null) {
        this.store.updateSources(sources);
    }

    /** @input */
    public readonly hideSecurity: boolean;

    public readonly headlines$: Observable<NewsHeadlineDataRows>;
    public readonly hasMoreData$: Observable<boolean>;
    public readonly noMoreData$: Observable<boolean>;

    private readonly mouseInside$ = new BehaviorSubject(false);
    private readonly scrollAtTop$ = new BehaviorSubject(true);

    private readonly store: NewsStore;

    /** @ngInject */
    public constructor(private readonly $element: IRootElementService,
                       private readonly newsItemDialogService: NewsItemDialogService,
                       newsStoreFactory: NewsStoreFactory) {
        this.store = newsStoreFactory.create();
        this.headlines$ = this.buildHeadlines();
        this.hasMoreData$ = this.store
                                .noMoreHistory()
                                .map((noHistory) => !noHistory)
                                .publishReplay(1)
                                .refCount();
        this.noMoreData$ = this.store
                               .noMoreHistory();
    }

    private buildHeadlines(): Observable<NewsHeadlineDataRows> {

        const shouldPause$ = Observable.combineLatest(this.scrollAtTop$, this.mouseInside$)
                                       .map(([atTop, inside]) => atTop && inside)
                                       .distinctUntilChanged();

        return Observable.combineLatest(this.store.headlines, shouldPause$)
                         .filter(([_, shouldPause]) => !shouldPause)
                         .map(([headlines]) => headlines)
                         .publishReplay(1)
                         .refCount();
    }

    public $postLink(): void {
        const scrollEl = this.$element.find("div[slim-scroll]")[0];
        scrollEl.addEventListener("scroll", () => {
            const topScroll = scrollEl.scrollTop === 0;
            this.scrollAtTop$.next(topScroll);
        });
        this.$element.on("mouseenter", () => this.mouseInside$.next(true));
        this.$element.on("mousemove", () => this.mouseInside$.next(true));
        this.$element.on("mouseleave", () => this.mouseInside$.next(false));
    }

    public loadMore() {
        const scrollEl = this.$element.find("div[slim-scroll]")[0];
        const scrollTop = scrollEl.scrollTop;
        const noMore$ = this.store
                            .noMoreHistory()
                            .filter(identity())
                            .first();
        this.store.loadMoreHistory();
        this.store
            .headlines
            .takeUntil(noMore$)
            .distinctUntilChanged((a, b) => this.historyUnchanged(a, b))
            .skip(1) // Previous value
            .take(1) // Next change
            .subscribe(() => {
                setTimeout(() => scrollEl.scrollTo({top: scrollTop}), 50);
            });
    }

    public isSeparatorRow(row: NewsHeadlineDataRow): boolean {
        return isDateSeparator(row);
    }

    public rowId(row: NewsHeadlineDataRow): string | NewsItemId {
        if (isDateSeparator(row)) {
            return row.date;
        } else {
            return row.id;
        }
    }

    /* Check if the previous and next history values have different oldest news values */
    private historyUnchanged(previous: NewsHeadlineDataRows, next: NewsHeadlineDataRows): boolean {
        const previousData = previous.filter(isHeadlineModel);
        const nextData = next.filter(isHeadlineModel);
        const oldestPrevious = ArrayUtil.min(previousData, (m1, m2) => m1.at.localeCompare(m2.at))
                                        .map((m) => m.id);
        const oldestNext = ArrayUtil.min(nextData, (m1, m2) => m1.at.localeCompare(m2.at))
                                    .map((m) => m.id);
        return oldestPrevious.equals(oldestNext);
    }

    public openHeadline(headline: NewsHeadlineViewModel) {
        this.store.markRead(headline.id);
        this.newsItemDialogService.open(headline);
    }

    public markRead(headline: NewsHeadlineViewModel) {
        this.store.markRead(headline.id);
    }
}
